import {isTopicSessionComplete, NEEDS_WORK_BELOW} from './topic-sessions'

/*
 * Exam readiness, as five dashes (171).
 *
 * A DIFFERENT MEASURE to calcReadiness in util/mini (071), which is questions-ever-correct over
 * total questions. That one answers "how much of the material have I got right"; this one answers
 * "how much of the course have I BANKED", and only a topic finished at a passing score counts. Both
 * exist on purpose — the old one still feeds nothing on this screen, but it is exported and used
 * elsewhere, so it is left alone.
 *
 * Summed from TOPICS at both levels: the prepkit meter over every leaf topic in its primary
 * products, a section's meter over the leaf topics inside that section. Supplemental products are
 * excluded by the caller, which is where the primary/supplemental split already lives.
 */

const DASH_COUNT = 5
const DASH_PERCENT = 100 / DASH_COUNT  // 20
const PASSING = NEEDS_WORK_BELOW       // 75 — the same bar the rest of the dashboard grades against
const STAR_AT = 90

/*
 * A topic's accuracy, 0-100.
 *
 * Same formula as the dashboard's topicCounts. Repeated rather than imported because that one lives
 * inside the dashboard component file and importing back out of it would make the cycle
 * readiness -> dashboard -> readiness.
 */
const topicAccuracy = (topic) => {
	const answered = topic.answeredCount || 0
	if (!answered) return 0
	return Math.round(((topic.correctCount || 0) / answered) * 100)
}

// banked: finished, and finished well enough to count
const topicPassed = (topic) => isTopicSessionComplete(topic) && topicAccuracy(topic) >= PASSING

/*
 * How many dashes are earned, and whether the next one is under way.
 *
 * floor, so a dash only lights when it is fully paid for: at 70% that is three solid dashes (60%)
 * and the fourth in progress, never four solid.
 */
const dashesFor = (percent) => {
	const full = Math.min(DASH_COUNT, Math.floor(percent / DASH_PERCENT))
	const partial = full < DASH_COUNT && percent - full * DASH_PERCENT > 0
	/*
	 * How far into the CURRENT dash, 0 to just under 1. The tagline needs it: "let's get started" is
	 * wrong for someone two topics in, and "one bar to go" is wrong for someone almost through their
	 * fourth, so the wording has to know about progress inside a dash and not only whole dashes.
	 *
	 * Exact at 0 rather than nearly-zero, because `percent` is a rounded integer and DASH_PERCENT
	 * divides 100 — so `intoBar === 0` is a safe test for "nothing at all in this dash".
	 */
	const intoBar = full >= DASH_COUNT ? 0 : (percent - full * DASH_PERCENT) / DASH_PERCENT
	return {full, partial, intoBar}
}

/*
 * Readiness over a set of leaf topics.
 *
 * `percent` is the proportion of topics BANKED, which is what the dashes show. The star is driven by
 * something else — the average score across finished topics — because the two say different things
 * and the design needs both: five solid dashes means "all of it is done to standard", and the star
 * means "and done well". Without that split, a full meter could not distinguish a course finished at
 * 76% from one finished at 95%.
 *
 * THE STAR REQUIRES EVERY TOPIC IN SCOPE TO BE FINISHED (175), not just a high average over whatever
 * has been finished so far. Without that it was awarded absurdly early: one topic of sixty-eight
 * finished at 95% is a 95% average, so the star appeared beside an empty meter — congratulating a
 * student for 1.5% of the course. Averaging over a subset the student chooses is not an achievement.
 *
 * Topics with no questions are excluded outright; they cannot be passed, so counting them in the
 * denominator would cap readiness below 100 for reasons the student cannot act on.
 */
const readinessFromTopics = (topics) => {
	const scored = (topics || []).filter(t => (t.questionCount || 0) > 0)
	const total = scored.length
	const completed = scored.filter(isTopicSessionComplete)
	const passed = scored.filter(topicPassed).length

	const percent = total ? Math.round((passed / total) * 100) : 0
	const avgScore = completed.length
		? Math.round(completed.reduce((sum, t) => sum + topicAccuracy(t), 0) / completed.length)
		: 0

	return {
		percent,
		passed,
		total,
		completed: completed.length,
		avgScore,
		// all of it finished, and finished well; see the note above
		star: total > 0 && completed.length === total && avgScore >= STAR_AT,
		...dashesFor(percent),
	}
}

/*
 * readinessTagline / readinessMessage lived here (171) and are gone (187).
 *
 * They wrote a different heading and explanation for each of nine rungs of the meter. The popover
 * now carries one supplied pair of lines at every rung, on the grounds that what a student opens it
 * to learn — what the bars mean — does not change as the bars fill. Nothing else called them.
 *
 * CELEBRATE_BELOW goes with them: it was the point those rungs split at and had no other caller.
 * `intoBar` is still computed by dashesFor, so how far into a bar a student is remains available to
 * anything that wants it.
 */

export {DASH_COUNT, DASH_PERCENT, PASSING, STAR_AT, topicAccuracy, topicPassed,
	dashesFor, readinessFromTopics}
